// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import { useBurnContext } from '@hooks/mutations/registry/burn/context/useBurnContext';
import { Burn } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Model/Burn';
import { TransferOffer } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Model/Transfer';
import { AllocationFactory } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/AllocationFactory';
import {
  RegistrarHoldingOperationsDelegation,
  RegistrarHoldingOperationsDelegation_Burn,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/HoldingOperationsDelegation';
import { Holding } from '@daml.js/dtc-registry-holding/lib/Dtc/Registry/Holding/V3/Holding';
import { DvpLegAllocation } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Holding/Allocation';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import { ContractId } from '@daml/types';
import { toDamlDisclosedContract } from '../../../../utils/common';
import { ERR_REQ_BURN, SUC_REQ_BURN } from '../../../../utils/strings';

interface Args {
  party: string;
  amount: string;
  source: string;
  instrumentId: string;
  holdingCids?: string[];
  reference?: string;
  delegationCid: string;
  transferOfferCids?: string[];
  allocationCids?: string[];
}

export const useRegistrarDelegatedBurn = () => {
  const getBurnContext = useBurnContext();
  const { submit: createBurnRequest } = useExerciseSubmission(
    RegistrarHoldingOperationsDelegation.RegistrarHoldingOperationsDelegation_Burn,
    SUC_REQ_BURN,
    ERR_REQ_BURN,
  );

  const now = new Date();
  return async (args: Args) => {
    const {
      source,
      party,
      amount,
      instrumentId,
      holdingCids,
      reference,
      delegationCid,
      transferOfferCids,
      allocationCids,
    } = args;

    const burnContext = await getBurnContext.mutateAsync({
      registrar: source,
      holder: party,
      instrumentId,
    });

    const burn: Burn = {
      instrumentId: {
        admin: source,
        id: instrumentId,
      },
      amount,
      holder: party,
      reference: reference || '',
      requestedAt: now.toISOString(),
      executeBefore: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days later
      meta: { values: {} },
    };

    const payload: RegistrarHoldingOperationsDelegation_Burn = {
      factoryCid: burnContext.factoryId as ContractId<AllocationFactory>,
      burn,
      holdingCids: holdingCids ? holdingCids.map((cid) => cid as ContractId<Holding>) : [],
      burnExtraArgs: {
        context: burnContext.choiceContext.choiceContextData as ChoiceContext,
        meta: { values: {} },
      },
      transferOfferCids: transferOfferCids?.length
        ? transferOfferCids.map((cid) => cid as ContractId<TransferOffer>)
        : null,
      allocationCids: allocationCids?.length
        ? allocationCids.map((cid) => cid as ContractId<DvpLegAllocation>)
        : null,
      allocationV2Cids: null,
    };
    const disclosedContracts =
      burnContext.choiceContext.disclosedContracts.map(toDamlDisclosedContract) || [];

    await createBurnRequest({
      cid: delegationCid as ContractId<RegistrarHoldingOperationsDelegation>,
      argument: payload,
      disclosedContracts,
    });
  };
};
